import { useCallback, useEffect, useMemo, useState } from 'react'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { toggleStyle } from 'Pods/ThreeDStudio/Store/actions/renderSettings'
import { closeStylePanel } from 'Pods/ThreeDStudio/Store/actions/ui'
import { MAX_RENDER_STYLES } from 'Pods/ThreeDStudio/Store/constants'

import { STYLE_TAG_ALL } from './constants'
import useStylePresets from './Hooks/useStylePresets'

export default function useStylePanel() {
  const { styles, isLoading, isError } = useStylePresets()
  const selectedStyles = useThreeDStudioStore(state => state.renderSettings.styles)
  const selectedStyleIds = useMemo(() => selectedStyles.map(style => style.id), [selectedStyles])
  const [query, setQuery] = useState('')
  const [activeTagId, setActiveTagId] = useState(STYLE_TAG_ALL)
  // Snapshotted at mount - the panel only mounts while open, so these are the styles
  // selected when it opened. Pinning those ids (rather than reading the live
  // selection) keeps the grid order stable for as long as the panel is up.
  const [pinnedStyleIds] = useState(selectedStyleIds)

  const visibleStyles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    const matches = styles.filter((style) => {
      const matchesTag = activeTagId === STYLE_TAG_ALL || style.tags.includes(activeTagId)
      const matchesQuery = !normalizedQuery || style.name.toLowerCase().includes(normalizedQuery)

      return matchesTag && matchesQuery
    })

    // The selected styles lead the grid, in selection order, so they are visible without
    // scrolling. One filtered out by the current tag/search is simply skipped.
    const pinned = pinnedStyleIds
      .map(id => matches.find(style => style.id === id))
      .filter(Boolean)

    return [...pinned, ...matches.filter(style => !pinnedStyleIds.includes(style.id))]
  }, [styles, query, activeTagId, pinnedStyleIds])

  // The panel is a non-modal dialog rendered next to the field that opened it, so it
  // has no focus trap; Escape is the only keyboard way out.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeStylePanel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleQueryChange = useCallback((event) => {
    setQuery(event.target.value)
  }, [])


  return {
    query,
    activeTagId,
    visibleStyles,
    selectedStyleIds,
    selectedCount: selectedStyleIds.length,
    isLimitReached: selectedStyleIds.length >= MAX_RENDER_STYLES,
    isLoading,
    isError,
    handleQueryChange,
    handleSelectTag: setActiveTagId,
    handleSelectStyle: toggleStyle,
  }
}
